import React, { useMemo } from "react";

import { formatCmOutcome, formatNumber, roundCmOutcome, useTranslation } from "utils";

import { CurrencyCode, NO_VALUE, RESILIENCE_UNLOCK_MIN_CROPS } from "consts";

import { Icon, Tooltip, Typography } from "components";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { formatWholeSignedPercent } from "pages/app/scenario-analysis/utils/comparisonFormatting";
import { SoilSightOutcomesByScenario, ViewMode } from "pages/app/scenario-analysis/utils/forecast";
import {
  ResilienceBandKey,
  cropsMatchBaseline,
  cumulativeGapByYear,
  findBreakEven,
  getResilienceCardBand,
  toGapBands,
} from "pages/app/scenario-analysis/utils/keyInsightCards";
import { countFilledCropYears } from "pages/app/scenario-analysis/utils/resilienceScorecard";
import { ScenarioComparison } from "pages/app/scenario-analysis/utils/scenarioComparison";
import { ScenarioResilienceByScenarioId } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

export interface KeyInsightCardsProps {
  comparison: ScenarioComparison;
  baseline: ScenarioData;
  subject: ScenarioData;
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  projectionYears: number;
  currency: CurrencyCode;
  resilienceScores: ScenarioResilienceByScenarioId;
  /** Switches the chart to the given view; `scenarioId` picks the scenario for single-scenario views. */
  onOpenChartView: (view: ViewMode, scenarioId?: string) => void;
  /** The view the chart is showing, so the card that opens it reads as the current position in the row. */
  activeView?: ViewMode;
}

type CardTone = "positive" | "negative" | "neutral";

const signTone = (value: number): CardTone => (value > 0 ? "positive" : value < 0 ? "negative" : "neutral");

/** Tone follows what is on screen: a figure shown as a whole percent must not be coloured once it reads "0%". */
const wholePercentTone = (pct: number | null, fallback: number): CardTone => (pct === null ? signTone(fallback) : signTone(Math.round(pct)));

/** Strong and good read as a gain, weak as a loss; moderate is neither, so it stays unstyled. */
const RESILIENCE_BAND_TONE: Record<ResilienceBandKey, CardTone> = { strong: "positive", good: "positive", moderate: "neutral", weak: "negative" };

/** Only bands reading clearly good or bad carry an arrow; "Moderate" is a level, not a direction. */
const RESILIENCE_BAND_ICON: Record<ResilienceBandKey, string | null> = {
  strong: "tabler/trending-up",
  good: "tabler/trending-up",
  moderate: null,
  weak: "tabler/trending-down",
};

interface InsightCardProps {
  label: string;
  tooltip: string;
  value: string;
  tone: CardTone;
  /** Sits on the value's baseline, e.g. the €/ha gap or "out of 10". */
  secondary?: React.ReactNode;
  /** Pinned to the bottom of the card, so bottom rows line up across the row. */
  bottom?: React.ReactNode;
  /** Replaces the figure when there is no number to show — a sentence does not read at display scale. */
  message?: string;
  onClick: () => void;
  /** The chart below is showing this card's view, so the row reads as navigation with a position. */
  isCurrent?: boolean;
  dataTestId: string;
}

const InsightCard = ({ label, tooltip, value, tone, secondary, bottom, message, onClick, isCurrent = false, dataTestId }: InsightCardProps) => (
  <button
    type="button"
    className={`key-insight-card${isCurrent ? " key-insight-card--current" : ""}`}
    aria-current={isCurrent || undefined}
    onClick={onClick}
    data-testid={dataTestId}
  >
    <span className="key-insight-card-label-row">
      <Typography variant="paragraph-m" className="key-insight-card-label">
        {label}
      </Typography>
      <Tooltip title={tooltip} placement="top">
        {/* A nested button would swallow the card's own click, so the affordance is a focusable span. */}
        <span
          className="key-insight-card-info"
          tabIndex={0}
          aria-label={tooltip}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          data-testid={`${dataTestId}-info`}
        >
          <Icon iconName="tabler/info-circle" size="s" />
        </span>
      </Tooltip>
    </span>

    {message ? (
      <Typography variant="paragraph-m" className="key-insight-card-message" data-testid={`${dataTestId}-message`}>
        {message}
      </Typography>
    ) : (
      <span className="key-insight-card-figure-row">
        <Typography variant="paragraph-m" className={`key-insight-card-value key-insight-card-value--${tone}`} key={value}>
          {value}
        </Typography>
        {secondary ? <span className="key-insight-card-secondary">{secondary}</span> : null}
      </span>
    )}

    <span className="key-insight-card-bottom">{bottom}</span>
  </button>
);

const ViewChartLink = ({ label }: { label: string }) => (
  <span className="key-insight-card-view-chart">
    {label}
    <Icon iconName="tabler/arrow-right" size="s" className="key-insight-card-view-chart-icon" />
  </span>
);

export const KeyInsightCards = ({
  comparison,
  baseline,
  subject,
  soilSightOutcomes,
  projectionYears,
  currency,
  resilienceScores,
  onOpenChartView,
  activeView,
}: KeyInsightCardsProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  const gaps = useMemo(
    () => cumulativeGapByYear(soilSightOutcomes?.[subject.id] ?? [], soilSightOutcomes?.[baseline.id] ?? [], projectionYears),
    [soilSightOutcomes, subject.id, baseline.id, projectionYears]
  );
  const breakEven = useMemo(() => findBreakEven(gaps), [gaps]);
  const bands = useMemo(() => toGapBands(gaps), [gaps]);
  const cropsMatch = useMemo(() => cropsMatchBaseline(baseline, subject), [baseline, subject]);

  const perHectare = (value: string) => t("scenarioComparison.perHectare", { value });
  const currencyOpts = { currency, currencyDisplay: "narrowSymbol" } as const;

  const marginTone = wholePercentTone(comparison.cumulativeMarginDeltaPct, comparison.cumulativeMarginDelta);

  const subjectTotal = roundCmOutcome(comparison.comparisonCumulativeMargin);
  const baselineTotal = roundCmOutcome(comparison.baselineCumulativeMargin);
  // Rounded once from the raw delta, so this matches the Difference view's endpoint and this card's own
  // percentage. The two totals below are context ("X against Y"), not a subtraction to reconcile against.
  const marginGap = perHectare(formatCmOutcome(comparison.cumulativeMarginDelta, { ...currencyOpts, signDisplay: "exceptZero" }));

  const breakEvenTone: CardTone = breakEven.year === null ? "negative" : "positive";
  const breakEvenValue = breakEven.year === null ? t("keyInsightCards.breaksEven.never") : t("keyInsightCards.breaksEven.year", { year: breakEven.year });
  // The bar already says which years are behind, so the depth of the dip belongs in the label's tooltip.
  const breakEvenTooltip =
    breakEven.dip < 0
      ? `${t("keyInsightCards.breaksEven.tooltip")} ${t("keyInsightCards.breaksEven.dipTooltip", {
          value: perHectare(formatCmOutcome(Math.abs(breakEven.dip), currencyOpts)),
        })}`
      : t("keyInsightCards.breaksEven.tooltip");

  const resilienceResult = resilienceScores[subject.id];
  const resilienceScore = resilienceResult ? resilienceResult.baseScore : null;
  const resilienceBand = resilienceScore === null ? null : getResilienceCardBand(resilienceScore);
  // Too short to score gets the scorecard's own message; a score the API returned is never withheld.
  const resilienceNeedsMoreCrops = resilienceScore === null && countFilledCropYears(subject.years) < RESILIENCE_UNLOCK_MIN_CROPS;

  return (
    <div className="key-insight-cards" data-testid="key-insight-cards">
      <InsightCard
        label={t("keyInsightCards.totalMargin.label", { years: projectionYears })}
        tooltip={t("keyInsightCards.totalMargin.tooltip")}
        value={formatWholeSignedPercent(comparison.cumulativeMarginDeltaPct)}
        tone={marginTone}
        secondary={
          <Typography variant="paragraph-m" className={`key-insight-card-secondary-figure key-insight-card-secondary-figure--${marginTone}`}>
            {marginGap}
          </Typography>
        }
        bottom={
          <Typography variant="paragraph-s" className="key-insight-card-bottom-text">
            {t("keyInsightCards.totalMargin.against", {
              scenario: perHectare(formatCmOutcome(subjectTotal, currencyOpts)),
              baseline: perHectare(formatCmOutcome(baselineTotal, currencyOpts)),
            })}
          </Typography>
        }
        onClick={() => onOpenChartView("cumulative")}
        isCurrent={activeView === "cumulative"}
        dataTestId="key-insight-card-total-margin"
      />

      <InsightCard
        label={t("keyInsightCards.breaksEven.label")}
        tooltip={breakEvenTooltip}
        value={breakEvenValue}
        tone={breakEvenTone}
        bottom={
          bands.length > 0 ? (
            <span className="key-insight-card-gap-bar" data-testid="key-insight-card-gap-bar" aria-hidden>
              {bands.map((band) => (
                <span
                  key={band.startYear}
                  className={`key-insight-card-gap-bar-segment key-insight-card-gap-bar-segment--${band.tone}`}
                  style={{ flexGrow: band.years }}
                  data-testid={`key-insight-card-gap-bar-${band.tone}`}
                />
              ))}
            </span>
          ) : null
        }
        onClick={() => onOpenChartView("difference")}
        isCurrent={activeView === "difference"}
        dataTestId="key-insight-card-breaks-even"
      />

      <InsightCard
        label={t("keyInsightCards.yieldChange.label")}
        tooltip={t("keyInsightCards.yieldChange.tooltip")}
        value={formatWholeSignedPercent(comparison.averageYieldDeltaPct)}
        tone={
          comparison.averageYieldDeltaPct === null || Math.round(comparison.averageYieldDeltaPct) === 0
            ? "neutral"
            : comparison.averageYieldDeltaPct > 0
            ? "positive"
            : "negative"
        }
        secondary={
          cropsMatch ? null : (
            <Typography variant="paragraph-s" className="key-insight-card-warning" data-testid="key-insight-card-crops-differ">
              {t("keyInsightCards.yieldChange.cropsDiffer")}
            </Typography>
          )
        }
        bottom={<ViewChartLink label={t("keyInsightCards.viewChart")} />}
        onClick={() => onOpenChartView("yield")}
        isCurrent={activeView === "yield"}
        dataTestId="key-insight-card-yield-change"
      />

      <InsightCard
        label={t("keyInsightCards.resilience.label")}
        tooltip={t("keyInsightCards.resilience.tooltip")}
        /* One decimal, the same precision as the scorecard — a whole number hid the gap between 7.5 and 8.4. */
        value={resilienceScore === null ? NO_VALUE : formatNumber({ number: resilienceScore, decimalPlaces: 1 })}
        tone={resilienceBand === null ? "neutral" : RESILIENCE_BAND_TONE[resilienceBand]}
        message={resilienceNeedsMoreCrops ? t("resilienceScorecard.emptyState.title") : undefined}
        secondary={
          resilienceBand ? (
            <>
              <Typography variant="paragraph-s" className="key-insight-card-out-of">
                {t("keyInsightCards.resilience.outOfTen")}
              </Typography>
              <span className={`key-insight-card-band key-insight-card-band--${resilienceBand}`} data-testid="key-insight-card-resilience-band">
                {RESILIENCE_BAND_ICON[resilienceBand] ? <Icon iconName={RESILIENCE_BAND_ICON[resilienceBand] as string} size="xs" /> : null}
                {t(`keyInsightCards.resilience.band.${resilienceBand}`)}
              </span>
            </>
          ) : null
        }
        bottom={<ViewChartLink label={t("keyInsightCards.viewChart")} />}
        /* Carries the subject: the scorecard shows one scenario, and it must be the one this card scored. */
        onClick={() => onOpenChartView("resilience", subject.id)}
        isCurrent={activeView === "resilience"}
        dataTestId="key-insight-card-resilience"
      />
    </div>
  );
};

export default KeyInsightCards;
